/**
 * /evals — Eval Runner + A/B Compare (auth-gated).
 * =================================================
 * Three tabs:
 *   1. Datasets — list / create eval datasets (input + expected_output pairs)
 *   2. Runs     — list past eval runs; click for per-row results
 *   3. Compare  — A/B-test two prompt variants over the same dataset
 *
 * MVP keeps everything synchronous — datasets are capped at 200 items
 * server-side; switch to a job queue later when customers go big.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Beaker, Plus, ArrowLeft, Play, GitCompareArrows, Loader2, Trophy, CheckCircle2, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtCost = (n) => n >= 0.01 ? `$${n.toFixed(4)}` : `$${(n || 0).toFixed(6)}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const DEFAULT_MODELS = ['gpt-5.2', 'gpt-5.1', 'gpt-5-mini', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'gemini-3.1-pro-preview', 'gemini-3-flash-preview'];

const EvalRunner = () => {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [runs, setRuns] = useState([]);
  const [builtins, setBuiltins] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  // create dataset
  const [dsOpen, setDsOpen] = useState(false);
  const [ds, setDs] = useState({ name: '', description: '', items: [{ input: { input: '' }, expected_output: '' }] });

  // run eval
  const [runOpen, setRunOpen] = useState(false);
  const [runForm, setRunForm] = useState({
    name: '', dataset_id: '', model: 'claude-sonnet-4-6',
    inline_system_message: 'You are a helpful assistant.',
    inline_user_template: 'Answer: {{input}}',
    criteria: [],
  });
  const [running, setRunning] = useState(false);

  // detail
  const [runDetail, setRunDetail] = useState(null);
  const [runDetailOpen, setRunDetailOpen] = useState(false);

  // compare
  const [cmpOpen, setCmpOpen] = useState(false);
  const [cmpForm, setCmpForm] = useState({
    name: '', dataset_id: '',
    variant_a: { name: 'A', model: 'claude-haiku-4-5-20251001', inline_system_message: 'Be terse.', inline_user_template: '{{input}}' },
    variant_b: { name: 'B', model: 'claude-sonnet-4-6', inline_system_message: 'Be terse.', inline_user_template: '{{input}}' },
    criteria: [],
  });
  const [cmpResult, setCmpResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, r, b, m] = await Promise.all([
        axios.get(`${API}/evals/datasets`, auth()),
        axios.get(`${API}/evals/runs`, auth()),
        axios.get(`${API}/evals/criteria/builtin`),
        axios.get(`${API}/prompts/models`),
      ]);
      setDatasets(d.data.items || []);
      setRuns(r.data.items || []);
      setBuiltins(b.data.items || []);
      setModels(m.data.models || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const onCreateDataset = async () => {
    if (!ds.name || ds.items.length === 0) { toast.error('Name + at least 1 item'); return; }
    try {
      await axios.post(`${API}/evals/datasets`, ds, auth());
      toast.success(`Dataset "${ds.name}" created`);
      setDsOpen(false);
      setDs({ name: '', description: '', items: [{ input: { input: '' }, expected_output: '' }] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };

  const toggleCriterion = (typ, form, setForm) => {
    const exists = (form.criteria || []).find((c) => c.type === typ);
    if (exists) setForm({ ...form, criteria: form.criteria.filter((c) => c.type !== typ) });
    else {
      const builtin = builtins.find((b) => b.id === typ);
      setForm({ ...form, criteria: [...(form.criteria || []), { type: typ, name: builtin?.name || typ, config: {} }] });
    }
  };

  const onRun = async () => {
    if (!runForm.dataset_id) { toast.error('Pick a dataset'); return; }
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/evals/runs`, runForm, auth());
      toast.success(`Run finished · pass rate ${(data.aggregate.pass_rate * 100).toFixed(0)}%`);
      setRunOpen(false);
      setRunDetail(data);
      setRunDetailOpen(true);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Run failed'); }
    finally { setRunning(false); }
  };

  const onCompare = async () => {
    if (!cmpForm.dataset_id) { toast.error('Pick a dataset'); return; }
    setComparing(true);
    setCmpResult(null);
    try {
      const { data } = await axios.post(`${API}/evals/compare`, cmpForm, auth());
      setCmpResult(data);
      toast.success(`A/B done · A win ${(data.a_win_rate * 100).toFixed(0)}% · B win ${(data.b_win_rate * 100).toFixed(0)}%`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Compare failed'); }
    finally { setComparing(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="evals-page">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/prompt-playground" className="text-slate-600 hover:text-slate-900">Playground</Link>
            <Link to="/prompts" className="text-slate-600 hover:text-slate-900">Prompts</Link>
            <Link to="/traces" className="text-slate-600 hover:text-slate-900">Traces</Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3">
          <Beaker className="w-3 h-3 mr-1.5 inline" /> Evals + A/B compare
        </Badge>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-1" data-testid="evals-h1">Evaluations</h1>
        <p className="text-slate-400 mb-8">Did your prompt change actually help? Rank two variants. With math.</p>

        <Tabs defaultValue="datasets">
          <TabsList className="bg-slate-900 border border-slate-800" data-testid="evals-tabs">
            <TabsTrigger value="datasets" data-testid="evals-tab-datasets">Datasets ({datasets.length})</TabsTrigger>
            <TabsTrigger value="runs" data-testid="evals-tab-runs">Runs ({runs.length})</TabsTrigger>
            <TabsTrigger value="compare" data-testid="evals-tab-compare">A/B Compare</TabsTrigger>
          </TabsList>

          {/* DATASETS */}
          <TabsContent value="datasets" className="mt-6">
            <div className="flex justify-end mb-4 gap-2">
              <Dialog open={dsOpen} onOpenChange={setDsOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="evals-create-dataset-btn"><Plus className="w-4 h-4 mr-2" /> New dataset</Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
                  <DialogHeader><DialogTitle>New dataset</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Input placeholder="Name" value={ds.name} onChange={(e) => setDs({ ...ds, name: e.target.value })} className="bg-white border-slate-300" data-testid="dataset-name-input" />
                    <Input placeholder="Description (optional)" value={ds.description} onChange={(e) => setDs({ ...ds, description: e.target.value })} className="bg-white border-slate-300" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-2">Items (input → expected_output)</div>
                      {ds.items.map((it, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                          <Textarea placeholder='input (your {{input}} value)' value={it.input.input || ''} onChange={(e) => { const items = [...ds.items]; items[i].input = { input: e.target.value }; setDs({ ...ds, items }); }} className="bg-white border-slate-300 min-h-[60px] font-mono text-xs" data-testid={`dataset-input-${i}`} />
                          <Textarea placeholder='expected_output' value={it.expected_output} onChange={(e) => { const items = [...ds.items]; items[i].expected_output = e.target.value; setDs({ ...ds, items }); }} className="bg-white border-slate-300 min-h-[60px] font-mono text-xs" data-testid={`dataset-expected-${i}`} />
                        </div>
                      ))}
                      <Button size="sm" variant="outline" className="border-slate-700 text-slate-600 hover:bg-slate-800" onClick={() => setDs({ ...ds, items: [...ds.items, { input: { input: '' }, expected_output: '' }] })} data-testid="dataset-add-item-btn">+ Add row</Button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setDsOpen(false)}>Cancel</Button>
                    <Button onClick={onCreateDataset} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="dataset-create-submit">Create</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={runOpen} onOpenChange={setRunOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10" data-testid="evals-run-btn"><Play className="w-4 h-4 mr-2" /> Run eval</Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
                  <DialogHeader><DialogTitle>Run an eval</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Input placeholder="Run name" value={runForm.name} onChange={(e) => setRunForm({ ...runForm, name: e.target.value })} className="bg-white border-slate-300" data-testid="run-name-input" />
                    <select value={runForm.dataset_id} onChange={(e) => setRunForm({ ...runForm, dataset_id: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded p-2 text-sm text-slate-200" data-testid="run-dataset-select">
                      <option value="">— Pick dataset —</option>
                      {datasets.map((d) => <option key={d.dataset_id} value={d.dataset_id}>{d.name} ({d.item_count})</option>)}
                    </select>
                    <select value={runForm.model} onChange={(e) => setRunForm({ ...runForm, model: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded p-2 text-sm text-slate-200" data-testid="run-model-select">
                      {DEFAULT_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <Textarea value={runForm.inline_system_message} onChange={(e) => setRunForm({ ...runForm, inline_system_message: e.target.value })} className="bg-white border-slate-300 min-h-[60px] font-mono text-sm" placeholder="System message" />
                    <Textarea value={runForm.inline_user_template} onChange={(e) => setRunForm({ ...runForm, inline_user_template: e.target.value })} className="bg-white border-slate-300 min-h-[80px] font-mono text-sm" placeholder="User template, e.g. {{input}}" data-testid="run-template-input" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-1.5">Criteria</div>
                      <div className="flex flex-wrap gap-2">
                        {builtins.map((b) => {
                          const on = (runForm.criteria || []).find((c) => c.type === b.id);
                          return <button key={b.id} type="button" onClick={() => toggleCriterion(b.id, runForm, setRunForm)} className={`text-xs px-2.5 py-1 rounded border ${on ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'}`} data-testid={`run-criterion-${b.id}`}>{b.name}</button>;
                        })}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setRunOpen(false)}>Cancel</Button>
                    <Button onClick={onRun} disabled={running} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="run-submit-btn">
                      {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : <><Play className="w-4 h-4 mr-2" /> Run</>}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="datasets-list">
              {datasets.length === 0 ? <Card className="bg-white border-slate-200 shadow-sm border-dashed sm:col-span-2"><CardContent className="p-10 text-center text-slate-500">No datasets yet. Create one above.</CardContent></Card> :
                datasets.map((d) => (
                  <Card key={d.dataset_id} className="bg-white border-slate-200 shadow-sm" data-testid={`dataset-card-${d.dataset_id}`}>
                    <CardContent className="p-5">
                      <h3 className="font-bold text-slate-100">{d.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{d.description || 'No description'}</p>
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500"><span>{d.item_count} items</span><span>{fmtDate(d.created_at)}</span></div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </TabsContent>

          {/* RUNS */}
          <TabsContent value="runs" className="mt-6">
            <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="evals-runs-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Name</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Model</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Pass rate</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Cost</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">p95 lat</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.length === 0 ? <tr><td colSpan="6" className="text-center text-slate-500 py-10">No runs yet.</td></tr> :
                      runs.map((r) => (
                        <tr key={r.run_id} className="border-b border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={async () => { const { data } = await axios.get(`${API}/evals/runs/${r.run_id}`, auth()); setRunDetail(data); setRunDetailOpen(true); }} data-testid={`run-row-${r.run_id}`}>
                          <td className="px-4 py-3 text-slate-100 font-medium">{r.name}</td>
                          <td className="px-4 py-3 text-slate-600 font-mono text-[11px]">{r.model}</td>
                          <td className="px-4 py-3 text-emerald-300 font-mono">{((r.aggregate?.pass_rate || 0) * 100).toFixed(0)}%</td>
                          <td className="px-4 py-3 text-emerald-300 font-mono">{fmtCost(r.aggregate?.total_cost_usd || 0)}</td>
                          <td className="px-4 py-3 text-slate-600 font-mono">{r.aggregate?.p95_latency_ms || 0}ms</td>
                          <td className="px-4 py-3 text-slate-500 text-[11px]">{fmtDate(r.started_at)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMPARE */}
          <TabsContent value="compare" className="mt-6">
            <Card className="bg-white border-slate-200 shadow-sm mb-4">
              <CardContent className="p-5 space-y-3">
                <Input placeholder="Comparison name" value={cmpForm.name} onChange={(e) => setCmpForm({ ...cmpForm, name: e.target.value })} className="bg-white border-slate-300" data-testid="cmp-name-input" />
                <select value={cmpForm.dataset_id} onChange={(e) => setCmpForm({ ...cmpForm, dataset_id: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded p-2 text-sm text-slate-200" data-testid="cmp-dataset-select">
                  <option value="">— Pick dataset —</option>
                  {datasets.map((d) => <option key={d.dataset_id} value={d.dataset_id}>{d.name} ({d.item_count})</option>)}
                </select>
                {['variant_a', 'variant_b'].map((k) => (
                  <div key={k} className={`p-3 rounded border ${k === 'variant_a' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-emerald-500/30 bg-emerald-500/5'}`}>
                    <div className="text-[11px] uppercase tracking-wider font-bold mb-1.5 text-slate-600">Variant {k === 'variant_a' ? 'A' : 'B'}</div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Input placeholder="Variant name" value={cmpForm[k].name} onChange={(e) => setCmpForm({ ...cmpForm, [k]: { ...cmpForm[k], name: e.target.value } })} className="bg-white border-slate-300" data-testid={`cmp-${k}-name`} />
                      <select value={cmpForm[k].model} onChange={(e) => setCmpForm({ ...cmpForm, [k]: { ...cmpForm[k], model: e.target.value } })} className="bg-slate-950 border border-slate-800 rounded p-2 text-sm text-slate-200" data-testid={`cmp-${k}-model`}>
                        {DEFAULT_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <Textarea placeholder="System message" value={cmpForm[k].inline_system_message} onChange={(e) => setCmpForm({ ...cmpForm, [k]: { ...cmpForm[k], inline_system_message: e.target.value } })} className="bg-white border-slate-300 mb-2 min-h-[40px] font-mono text-xs" />
                    <Textarea placeholder="User template" value={cmpForm[k].inline_user_template} onChange={(e) => setCmpForm({ ...cmpForm, [k]: { ...cmpForm[k], inline_user_template: e.target.value } })} className="bg-white border-slate-300 min-h-[60px] font-mono text-xs" data-testid={`cmp-${k}-template`} />
                  </div>
                ))}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-1.5">Pass criteria (applied to both variants)</div>
                  <div className="flex flex-wrap gap-2">
                    {builtins.map((b) => {
                      const on = (cmpForm.criteria || []).find((c) => c.type === b.id);
                      return <button key={b.id} type="button" onClick={() => toggleCriterion(b.id, cmpForm, setCmpForm)} className={`text-xs px-2.5 py-1 rounded border ${on ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'}`} data-testid={`cmp-criterion-${b.id}`}>{b.name}</button>;
                    })}
                  </div>
                </div>
                <Button onClick={onCompare} disabled={comparing} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="cmp-run-btn">
                  {comparing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : <><GitCompareArrows className="w-4 h-4 mr-2" /> Run A/B</>}
                </Button>
              </CardContent>
            </Card>

            {cmpResult && (
              <Card className="bg-slate-900 text-slate-100 border-emerald-500/30" data-testid="cmp-result">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-5 h-5 text-emerald-400" />
                    <h2 className="text-xl font-bold text-white">{cmpResult.name}</h2>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3 text-center">
                    <Card className="bg-emerald-500/10 border-emerald-500/40">
                      <CardContent className="p-4">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">A wins · {cmpResult.a.name}</div>
                        <div className="text-3xl font-bold text-emerald-300 font-mono mt-1">{(cmpResult.a_win_rate * 100).toFixed(0)}%</div>
                        <div className="text-[11px] text-slate-400 mt-1">{cmpResult.a.model} · {fmtCost(cmpResult.a.total_cost_usd)} total</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800 border-slate-700">
                      <CardContent className="p-4">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Ties</div>
                        <div className="text-3xl font-bold text-slate-400 font-mono mt-1">{(cmpResult.tie_rate * 100).toFixed(0)}%</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-emerald-500/10 border-emerald-500/40">
                      <CardContent className="p-4">
                        <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold">B wins · {cmpResult.b.name}</div>
                        <div className="text-3xl font-bold text-cyan-300 font-mono mt-1">{(cmpResult.b_win_rate * 100).toFixed(0)}%</div>
                        <div className="text-[11px] text-slate-400 mt-1">{cmpResult.b.model} · {fmtCost(cmpResult.b.total_cost_usd)} total</div>
                      </CardContent>
                    </Card>
                  </div>
                  <details className="mt-2">
                    <summary className="text-sm text-slate-400 cursor-pointer hover:text-emerald-300 select-none">Per-row breakdown ({cmpResult.row_count} rows)</summary>
                    <div className="mt-3 max-h-[400px] overflow-y-auto space-y-2">
                      {cmpResult.pairs.map((p, i) => (
                        <div key={i} className="text-xs bg-slate-950 border border-slate-800 rounded p-2">
                          <div className="text-slate-500 mb-1.5">#{i + 1} · winner: <span className={p.winner === 'A' ? 'text-emerald-300' : p.winner === 'B' ? 'text-cyan-300' : 'text-slate-400'}>{p.winner}</span></div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="border-l-2 border-emerald-500/40 pl-2"><div className="text-emerald-400 text-[10px] mb-0.5 flex items-center gap-1">{p.a.pass ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3 text-rose-400" />} A · {fmtCost(p.a.cost)} · {p.a.latency_ms}ms</div><pre className="text-slate-400 font-mono text-[11px] whitespace-pre-wrap">{p.a.output?.slice(0, 200) || '(empty)'}</pre></div>
                            <div className="border-l-2 border-emerald-500/40 pl-2"><div className="text-cyan-400 text-[10px] mb-0.5 flex items-center gap-1">{p.b.pass ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3 text-rose-400" />} B · {fmtCost(p.b.cost)} · {p.b.latency_ms}ms</div><pre className="text-slate-400 font-mono text-[11px] whitespace-pre-wrap">{p.b.output?.slice(0, 200) || '(empty)'}</pre></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Run detail dialog */}
        <Dialog open={runDetailOpen} onOpenChange={setRunDetailOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-4xl">
            <DialogHeader><DialogTitle>{runDetail?.name}</DialogTitle></DialogHeader>
            {runDetail && (
              <>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <Card className="bg-white border-slate-300"><CardContent className="p-3"><div className="text-[10px] text-slate-500 uppercase">Pass rate</div><div className="text-lg font-mono text-emerald-300">{((runDetail.aggregate?.pass_rate || 0) * 100).toFixed(0)}%</div></CardContent></Card>
                  <Card className="bg-white border-slate-300"><CardContent className="p-3"><div className="text-[10px] text-slate-500 uppercase">Total cost</div><div className="text-lg font-mono text-emerald-300">{fmtCost(runDetail.aggregate?.total_cost_usd || 0)}</div></CardContent></Card>
                  <Card className="bg-white border-slate-300"><CardContent className="p-3"><div className="text-[10px] text-slate-500 uppercase">p50</div><div className="text-lg font-mono text-slate-300">{runDetail.aggregate?.p50_latency_ms}ms</div></CardContent></Card>
                  <Card className="bg-white border-slate-300"><CardContent className="p-3"><div className="text-[10px] text-slate-500 uppercase">p95</div><div className="text-lg font-mono text-slate-300">{runDetail.aggregate?.p95_latency_ms}ms</div></CardContent></Card>
                </div>
                <div className="max-h-[60vh] overflow-y-auto space-y-2">
                  {(runDetail.rows || []).map((row, i) => (
                    <div key={i} className={`text-xs p-2 rounded border ${row.pass ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {row.pass ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-rose-400" />}
                        <span className="text-slate-600 font-mono">#{i + 1}</span>
                        <span className="text-slate-500 ml-auto">{row.latency_ms}ms · {fmtCost(row.cost_usd)}</span>
                      </div>
                      <pre className="text-slate-600 font-mono whitespace-pre-wrap text-[11px]">{(row.output || '').slice(0, 400)}</pre>
                      {row.expected_output && <div className="text-slate-500 text-[10px] mt-1">Expected: <span className="text-slate-400">{row.expected_output.slice(0, 80)}</span></div>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default EvalRunner;
